import {
  buildDefaultFinancialAidCycleForYear,
  DEFAULT_SETTINGS_STATE,
  SettingsStateSchema,
  upsertFinancialAidCycle,
  type SettingsState,
  type TuitionRateCategory,
  type TuitionRateCategoryBand,
  type TuitionRateCategoryFee,
  type TuitionPaymentPlan,
} from '@triedge/staff-settings-domain';
import { buildDefaultModuleGovernanceState } from '../platform-governance/moduleGovernanceCatalog';

type DomainRecord = Record<string, unknown>;

const asRecord = (value: unknown): DomainRecord =>
  value && typeof value === 'object' && !Array.isArray(value)
    ? (value as DomainRecord)
    : {};

export const cloneSettingsState = (value: SettingsState): SettingsState => {
  if (typeof structuredClone === 'function') {
    return structuredClone(value);
  }
  return JSON.parse(JSON.stringify(value)) as SettingsState;
};

export const buildDefaultYstnjSettingsState = (): SettingsState => {
  const next = cloneSettingsState(DEFAULT_SETTINGS_STATE);
  next.financialAid = upsertFinancialAidCycle(
    next.financialAid,
    buildDefaultFinancialAidCycleForYear(next.financialAid),
    { setPrimary: true },
  );
  next.governance = buildDefaultModuleGovernanceState();
  return next;
};

const normalizeToken = (value: unknown): string =>
  String(value ?? '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');

const normalizeLegacyGender = (value: unknown): TuitionRateCategoryBand['gender'] => {
  const token = normalizeToken(value);
  if (token === 'boys' || token === 'boy' || token === 'male' || token === 'm') return 'boys';
  if (token === 'girls' || token === 'girl' || token === 'female' || token === 'f') return 'girls';
  return 'all';
};

const toTextArray = (value: unknown): string[] => {
  if (!Array.isArray(value)) return [];
  return value
    .map((entry) => String(entry ?? '').trim())
    .filter(Boolean);
};

const gradeKeyVariants = (value: number): string[] => [String(value), `grade_${value}`];

const resolveLegacyGradeKeys = (label: unknown): string[] => {
  const token = normalizeToken(label);
  if (!token) return [];
  if (token.includes('nursery')) return ['nursery'];
  if (token.includes('kindergarten')) return ['kindergarten'];
  if (token.includes('pre1a') || token.includes('pre_1a') || token.includes('pre1_a')) {
    return ['pre1a', 'pre1_a'];
  }

  const rangeMatch = token.match(/grades?_(\d+)_?(\d+)?/);
  if (rangeMatch?.[1]) {
    const start = Number(rangeMatch[1]);
    const end = Number(rangeMatch[2] ?? rangeMatch[1]);
    if (Number.isFinite(start) && Number.isFinite(end)) {
      const keys: string[] = [];
      for (let grade = Math.min(start, end); grade <= Math.max(start, end); grade += 1) {
        keys.push(...gradeKeyVariants(grade));
      }
      return keys;
    }
  }

  const numericMatch = token.match(/(?:^|_)grade_?(\d+)|^(\d+)$/);
  const numericGrade = Number(numericMatch?.[1] ?? numericMatch?.[2]);
  if (Number.isFinite(numericGrade) && numericGrade > 0) {
    return gradeKeyVariants(numericGrade);
  }

  return [token];
};

const resolveLegacyFeeAmountType = (fee: SettingsState['fees'][number]): TuitionRateCategoryFee['amountType'] => {
  const record = asRecord(fee);
  const ext = asRecord(record.ext);
  const token = normalizeToken(record.amountType ?? record.amount_type ?? record.scope ?? ext.amountType ?? ext.amount_type ?? ext.scope);
  if (token.includes('family')) {
    return 'per_family';
  }
  if (token.includes('child') || token.includes('student')) {
    return 'per_child';
  }
  return 'per_child';
};

const resolveLegacyFeeExemptGradeKeys = (fee: SettingsState['fees'][number]): string[] => {
  const record = asRecord(fee);
  const ext = asRecord(record.ext);
  return [
    ...toTextArray(record.exemptGradeKeys),
    ...toTextArray(record.exempt_grade_keys),
    ...toTextArray(ext.exemptGradeKeys),
    ...toTextArray(ext.exempt_grade_keys),
  ];
};

const buildLegacyFeeRateDescription = (
  fee: SettingsState['fees'][number],
  amountType: TuitionRateCategoryFee['amountType'],
): string => {
  const amount = Number(fee.amount ?? 0).toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
  return `$${amount} ${amountType === 'per_family' ? 'per family' : 'per student'}`;
};

const resolveLegacyFeeDueDate = (
  fee: SettingsState['fees'][number],
): string | null => {
  const record = asRecord(fee);
  const ext = asRecord(record.ext);
  const candidates = [
    record.dueDate,
    record.defaultDueDate,
    record.due_date,
    record.date,
    ext.dueDate,
    ext.defaultDueDate,
    ext.due_date,
    ext.date,
  ];
  for (const candidate of candidates) {
    const normalized = String(candidate ?? '').trim();
    if (normalized) {
      return normalized;
    }
  }
  return null;
};

const materializeLegacyTuitionRateCategory = (
  state: SettingsState,
): TuitionRateCategory | null => {
  const tuitionRates = state.tuitionRates
    .map((rate): TuitionRateCategoryBand | null => {
      const gradeKeys = resolveLegacyGradeKeys(rate.gradeLabel);
      const amount = Number(rate.amount ?? 0);
      if (gradeKeys.length === 0 || !Number.isFinite(amount)) {
        return null;
      }
      return {
        id: String(rate.id || `legacy-rate-${gradeKeys.join('-')}`).trim(),
        label: String(rate.gradeLabel || 'Tuition Rate').trim(),
        gradeKeys,
        gender: normalizeLegacyGender(rate.gender),
        amount,
      };
    })
    .filter((rate): rate is TuitionRateCategoryBand => Boolean(rate));

  if (tuitionRates.length === 0) {
    return null;
  }

  const feeSchedule = state.fees
    .map((fee): TuitionRateCategoryFee | null => {
      const amount = Number(fee.amount ?? 0);
      const id = String(fee.id || '').trim();
      const name = String(fee.name || id || 'Configured Fee').trim();
      if (!id || !Number.isFinite(amount)) {
        return null;
      }
      const amountType = resolveLegacyFeeAmountType(fee);
      return {
        id,
        name,
        entryType: normalizeToken(id || name) || 'configured_fee',
        rateDescription: buildLegacyFeeRateDescription(fee, amountType),
        amount,
        amountType,
        exemptGradeKeys: resolveLegacyFeeExemptGradeKeys(fee),
        dueDate: resolveLegacyFeeDueDate(fee),
      };
    })
    .filter((fee): fee is TuitionRateCategoryFee => Boolean(fee));

  return {
    id: 'legacy-admissions-default',
    label: 'Admissions Defaults',
    description: 'Uses the tuition and fee setup from Admissions settings.',
    flatRateMode: false,
    flatRateAmount: null,
    tuitionRates,
    feeSchedule,
    multiChildDiscountEligible: true,
    financialAidEligible: true,
    staffDiscountEligible: true,
    chinuchDiscountEligible: true,
    fundraisingObligationEnabled: false,
    fundraisingAmountPerChild: 0,
  };
};

const resolveAcademicYearStartYear = (state: SettingsState): number => {
  const label = String(state.academicYear?.label ?? '').trim();
  const labelMatch = label.match(/^(\d{4})\s*[-/]/);
  if (labelMatch?.[1]) {
    return Number(labelMatch[1]);
  }
  const startDate = String(state.academicYear?.startDate ?? '').trim();
  const dateMatch = startDate.match(/^(\d{4})-\d{2}-\d{2}$/);
  if (dateMatch?.[1]) {
    return Number(dateMatch[1]);
  }
  return new Date().getFullYear();
};

const fallbackTuitionPaymentPlans = (state: SettingsState): TuitionPaymentPlan[] => {
  const startYear = resolveAcademicYearStartYear(state);
  return [
    {
      id: 'pay-in-full',
      label: 'Pay in Full',
      installmentCount: 1,
      startDate: `${startYear}-08-01`,
      endDate: `${startYear}-08-01`,
      enabled: true,
    },
    {
      id: '10-month-plan',
      label: '10-Month Plan',
      installmentCount: 10,
      startDate: `${startYear}-08-01`,
      endDate: `${startYear + 1}-05-01`,
      enabled: true,
    },
    {
      id: '12-month-plan',
      label: '12-Month Plan',
      installmentCount: 12,
      startDate: `${startYear}-07-01`,
      endDate: `${startYear + 1}-06-01`,
      enabled: true,
    },
  ];
};

const materializeAdmissionsTuitionDefaults = (
  state: SettingsState,
  options: { materializeLegacyTuitionRows: boolean },
): SettingsState => {
  const next = cloneSettingsState(state);
  if (next.tuitionRateCategories.length === 0 && options.materializeLegacyTuitionRows) {
    const legacyCategory = materializeLegacyTuitionRateCategory(next);
    if (legacyCategory) {
      next.tuitionRateCategories = [legacyCategory];
    }
  }

  if (next.tuitionRateCategories.length > 0 && next.tuitionPaymentPlans.filter((plan) => plan.enabled).length === 0) {
    next.tuitionPaymentPlans = fallbackTuitionPaymentPlans(next);
  }

  if (
    next.tuitionRateCategories.length > 0
    && !next.tuitionRateCategories.some((category) => category.id === next.defaultTuitionRateCategoryId)
  ) {
    next.defaultTuitionRateCategoryId = next.tuitionRateCategories[0].id;
  }

  return next;
};

const applyDomainPatch = (base: DomainRecord, patch: DomainRecord): DomainRecord => {
  const next: DomainRecord = { ...base };
  for (const [key, patchValue] of Object.entries(patch)) {
    if (Array.isArray(patchValue)) {
      next[key] = typeof structuredClone === 'function'
        ? structuredClone(patchValue)
        : JSON.parse(JSON.stringify(patchValue));
      continue;
    }
    if (patchValue && typeof patchValue === 'object') {
      next[key] = applyDomainPatch(asRecord(next[key]), asRecord(patchValue));
      continue;
    }
    next[key] = patchValue;
  }
  return next;
};

export const parseSettingsState = (rawPayload: unknown): SettingsState => {
  let parsedPayload: unknown = rawPayload;
  if (typeof rawPayload === 'string') {
    try {
      parsedPayload = JSON.parse(rawPayload);
    } catch {
      parsedPayload = undefined;
    }
  }

  const normalizedPayload = applyDomainPatch(
    asRecord(buildDefaultYstnjSettingsState() as unknown),
    asRecord(parsedPayload),
  );
  const normalized = SettingsStateSchema.safeParse(normalizedPayload);
  if (normalized.success) {
    const parsedPayloadRecord = asRecord(parsedPayload);
    return materializeAdmissionsTuitionDefaults(normalized.data, {
      materializeLegacyTuitionRows:
        Array.isArray(parsedPayloadRecord.tuitionRates)
        && parsedPayloadRecord.tuitionRates.length > 0,
    });
  }

  return buildDefaultYstnjSettingsState();
};
